// Mock data based on EvolveBPM Credentials 2026 deck

export const brand = {
  name: 'EvolveBPM',
  tagline: 'Built for B2B. Powered by AI.',
  since: 'Since 2020',
  hq: 'HQ: New York, USA',
  primary: 'www.evolvebpm.com',
  platform: 'www.predictor.com',
};

export const differentiators = [
  {
    n: '01',
    title: 'We understand B2B sales — we get it.',
    desc: "Built by B2B sales practitioners, for B2B sales teams — so every strategy is grounded in how your buyers actually buy.",
    stat: '90%',
    statNum: 90,
    statSuffix: '%',
    statDesc: "of our team has 8+ years of B2B sales experience — we've sat in your reps' seats.",
    icon: 'Users',
    accent: '#4285F4',
  },
  {
    n: '02',
    title: 'AI adoption across the delivery value chain.',
    desc: 'AI embedded at every stage of delivery — so your campaigns get smarter, faster, and more efficient with every cycle, not just at launch.',
    stat: '+35%',
    statNum: 35,
    statSuffix: '%',
    statDesc: 'improvement in campaign performance within the first 60 days.',
    icon: 'Sparkles',
    accent: '#EA4335',
  },
  {
    n: '03',
    title: 'Multi-level intent signal processing.',
    desc: 'We decode buying intent at every level of the funnel, so your team spends time only on accounts that are actually ready to engage.',
    stat: '3×',
    statNum: 3,
    statSuffix: '×',
    statDesc: 'more accurate targeting by analyzing intent signals across 12+ buying stages.',
    icon: 'Radar',
    accent: '#FBBC05',
  },
  {
    n: '04',
    title: 'Proprietary first-party intent data.',
    desc: "Verified first-party intent data across every buying persona — giving you visibility into who's in-market before your competitors do.",
    stat: '4M+',
    statNum: 4,
    statSuffix: 'M+',
    statDesc: 'verified first-party intent data points across 20+ job functions and industries.',
    icon: 'Database',
    accent: '#34A853',
  },
  {
    n: '05',
    title: 'A lead scoring framework built for focus.',
    desc: 'A scoring model that tells your reps exactly who to call first — turning your pipeline into a priority list, not a guessing game.',
    stat: '4×',
    statNum: 4,
    statSuffix: '×',
    statDesc: 'faster conversion for prioritized leads vs. unscored pipeline.',
    icon: 'Target',
    accent: '#4ECDC4',
  },
  {
    n: '06',
    title: 'AI-driven quality, verified in real time.',
    desc: 'AI-validated leads, checked in real time through native LinkedIn and social integrations — so every lead that reaches your team is one worth pursuing.',
    stat: '98%',
    statNum: 98,
    statSuffix: '%',
    statDesc: 'lead accuracy rate through real-time AI validation and native LinkedIn verification.',
    icon: 'ShieldCheck',
    accent: '#6366F1',
  },
  {
    n: '07',
    title: 'One conversation, every channel.',
    desc: 'Coordinated engagement across paid, email, and tele-calling — so your buyers experience a consistent journey, not disconnected touchpoints.',
    stat: '2.5×',
    statNum: 2.5,
    statSuffix: '×',
    statDesc: 'higher engagement from multi-channel campaigns vs. single-channel outreach.',
    icon: 'Share2',
    accent: '#EC4899',
  },
];

export const signalFunctions = [
  'Marketing', 'Sales', 'IT & Engineering', 'Finance', 'Operations',
  'Procurement', 'HR', 'Product', 'Legal', 'Executive',
];

export const heroStats = [
  { label: 'Years of excellence', value: '6+', sub: 'Established 2020' },
  { label: 'Global team', value: '65+', sub: 'Multi-region delivery' },
  { label: 'Recognition', value: 'Top 50', sub: 'B2B Demand Gen partner' },
  { label: 'Headquarters', value: 'New York', sub: 'USA' },
];

export const capabilities = [
  {
    id: 'content-syndication',
    title: 'Content Syndication',
    desc: 'Targeted asset distribution across vertical audiences to capture intent at the top of funnel.',
    icon: 'FileText',
  },
  {
    id: 'sql-bant',
    title: 'SQL & BANT Leads',
    desc: 'Sales-ready leads validated against Budget, Authority, Need, and Timeline.',
    icon: 'Target',
  },
  {
    id: 'appointments',
    title: 'Appointment Setting',
    desc: 'Booked meetings with decision-makers, ready for your sales team to convert.',
    icon: 'CalendarCheck',
  },
  {
    id: 'linkedin-social',
    title: 'LinkedIn & Paid Social',
    desc: 'Precision ABM outreach and content amplification across the platforms that matter to B2B.',
    icon: 'Share2',
  },
  {
    id: 'whatsapp-sms',
    title: 'WhatsApp & SMS',
    desc: 'Signal-triggered conversational nurture with 95%+ open rates. Built on WhatsApp Business API.',
    icon: 'MessageCircle',
  },
  {
    id: 'webinars',
    title: 'Webinars & LIVE Events',
    desc: 'Curated executive experiences that convert intent into measurable pipeline acceleration.',
    icon: 'Radio',
  },
  {
    id: 'programmatic',
    title: 'Programmatic Display',
    desc: 'Predictor-driven placements that follow real intent, not static demographic segments.',
    icon: 'MonitorSmartphone',
  },
  {
    id: 'lead-scoring',
    title: 'Lead Scoring & Intelligence',
    desc: 'One scoring engine across every channel — your pipeline becomes a priority list, not a guess.',
    icon: 'Sparkles',
  },
];

export const capabilityImages = {
  'content-syndication': 'https://images.unsplash.com/photo-1499750310107-5fef28a66643?auto=format&fit=crop&w=1200&q=80',
  'sql-bant': 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1200&q=80',
  'programmatic': 'https://images.unsplash.com/photo-1522542550221-31fd19575a2d?auto=format&fit=crop&w=1200&q=80',
  'linkedin-social': 'https://images.unsplash.com/photo-1611944212129-29977ae1398c?auto=format&fit=crop&w=1200&q=80',
  'appointments': 'https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1200&q=80',
  'webinars': 'https://images.unsplash.com/photo-1591115765373-5207764f72e7?auto=format&fit=crop&w=1200&q=80',
  'whatsapp-sms': 'https://images.unsplash.com/photo-1611926653458-09294b3142bf?auto=format&fit=crop&w=1200&q=80',
  'lead-scoring': 'https://images.unsplash.com/photo-1590650153855-d9e808231d41?auto=format&fit=crop&w=1200&q=80',
};

export const capabilityMeta = {
  'content-syndication': { kpi: '2.4M imp/wk', tags: ['Whitepaper', 'Newsletter', 'Native'] },
  'sql-bant': { kpi: '9.3K SQLs/wk', tags: ['BANT', 'MQL', 'Handoff'] },
  'programmatic': { kpi: '480K clicks/mo', tags: ['Display', 'CTV', 'Native'] },
  'linkedin-social': { kpi: '5.7% CTR', tags: ['ABM', 'InMail', 'Document'] },
  'appointments': { kpi: '68 meetings/wk', tags: ['Calendar', 'C-suite', 'Warm handoff'] },
  'webinars': { kpi: '1,412 reg/event', tags: ['Live', 'On-demand', 'Panel'] },
  'whatsapp-sms': { kpi: '95%+ open rate', tags: ['Automated', 'Signal-triggered', 'Reminders'] },
  'lead-scoring': { kpi: '1.8s / lead', tags: ['First-party', 'Behavioral', 'Continuous'] },
};

/* Detailed "Learn more" content — sourced from EvolveBPM Capability Matrix docx */
export const capabilityDetails = {
  'content-syndication': {
    eyebrow: '01 / CONTENT SYNDICATION',
    headline: 'You don\u2019t get a list. You get intelligence.',
    lead: 'Content doesn\u2019t generate signal on its own. Distribution does. We put the right content in front of the right person, on the channel where they\u2019re already paying attention \u2014 then read what they do next.',
    features: [
      { title: 'Persona-first targeting', desc: 'Audiences segmented by role, industry, and stage \u2014 content built for that persona, not repurposed from a generic library.' },
      { title: 'Four coordinated channels', desc: 'Email, LinkedIn Ads, Paid Social, and tele-calling \u2014 each channel briefed to do one job, so nothing competes for attention.' },
      { title: 'Bespoke content hub', desc: 'Multiple assets hosted in one destination, so a visitor who came for one resource often engages with three.' },
      { title: 'AI chatbot, on schedule', desc: 'Structured, purpose-built qualifying questions asked at predefined intervals \u2014 not a generic \u2018how can I help.\u2019' },
      { title: 'Engagement \u2192 intelligence', desc: 'Every hub interaction feeds Predictor\u2019s scoring engine and surfaces directly on the lead card your team receives.' },
    ],
  },
  'sql-bant': {
    eyebrow: '02 / BANT-QUALIFIED LEADS',
    headline: 'The call isn\u2019t prospecting. It\u2019s confirming.',
    lead: 'Interest isn\u2019t a lead. Confirmed intent across all four parameters is. Every BANT lead is qualified with explicit information \u2014 Budget, Authority, Need, and Timeline \u2014 not inferred from behavior alone.',
    features: [
      { title: 'Digital qualification', desc: 'AI-guided BANT capture built directly into the content journey, as the prospect engages.' },
      { title: 'Tele-calling', desc: 'Trained callers confirm each parameter directly, in the prospect\u2019s own words.' },
      { title: 'Hybrid delivery', desc: 'Digital and tele-calling work the same account in sequence, each layer building on the other.' },
      { title: 'Warmed by content syndication', desc: 'Calls follow real engagement from the content hub \u2014 the conversation starts with context, not a cold open.' },
      { title: 'Explicit, not inferred', desc: 'Budget confirmed, Authority verified, Need stated, Timeline real. No parameter is skipped.' },
    ],
  },
  'appointments': {
    eyebrow: '03 / APPOINTMENT-SETTING',
    headline: 'Booked as your outreach. Not ours.',
    lead: 'A calendar invite is easy to fake. We make sure every one we book is worth keeping. The same intent-driven discipline that runs through everything else we do, applied all the way to the moment a meeting lands on your stakeholder\u2019s calendar.',
    features: [
      { title: 'Intent-driven targeting', desc: 'Every program starts with a detailed analysis of the target market and buyers \u2014 not a generic contact list.' },
      { title: 'Multi-channel warm-up', desc: 'LinkedIn, email, and paid social build recognition before any call is made.' },
      { title: 'Qualifying bar before the call', desc: 'Only leads meeting our qualifying score are shortlisted for tele-calling agents.' },
      { title: 'Senior SDRs & BDRs', desc: 'Experienced sellers, not entry-level dialers \u2014 practitioners who can hold a real, credible conversation.' },
      { title: 'Embedded delivery', desc: 'Agents can work with email access on your domain and direct calendar visibility, as an extension of your team.' },
    ],
  },
  'linkedin-social': {
    eyebrow: '04 / LINKEDIN & PAID SOCIAL',
    headline: 'The goal was never clicks. It\u2019s the outcome behind them.',
    lead: 'Reach is easy to buy. Attention that converts to pipeline is not. A dedicated digital practice built to turn clicks into defined, qualifiable outcomes \u2014 not just impressions.',
    features: [
      { title: 'LinkedIn Ads', desc: 'Precision-targeted campaigns by role, seniority, and industry.' },
      { title: 'Document Ads & InMail', desc: 'Native in-feed content and direct, personalized outreach to the people who matter.' },
      { title: 'Invite-only discussion forums', desc: 'Closed-group conversations that drive organic, deep engagement \u2014 participation, not just clicks.' },
      { title: 'Google, Meta & programmatic', desc: 'Capturing intent at the moment someone searches, and staying present as they browse elsewhere.' },
      { title: 'Continuous optimization', desc: 'Creative, targeting, and spend adjusted throughout the campaign, not set once and left alone.' },
    ],
  },
  'whatsapp-sms': {
    eyebrow: '05 / WHATSAPP & SMS',
    headline: 'Open rates upward of 95% on WhatsApp.',
    lead: 'Some conversations don\u2019t wait for an inbox to be checked. Built on the WhatsApp Business API and run fully automated \u2014 a fast, personal layer triggered by real signal, not a fixed send schedule.',
    features: [
      { title: 'Conversational nurture', desc: 'Automated flows that pick up exactly where content syndication or a call left off.' },
      { title: 'Instant content delivery', desc: 'Assets delivered straight into WhatsApp the moment a prospect requests them.' },
      { title: 'Appointment confirmations', desc: 'Automated reminders that protect the meeting slots our teams have already secured.' },
      { title: 'Timed SMS nudges', desc: 'Fast, triggered follow-ups fired by fresh signal \u2014 a hub visit, a missed call, a new download.' },
      { title: 'Feeding the same loop', desc: 'Every reply and click flows back into scoring, carrying the same weight as an email open on the lead card.' },
    ],
  },
  'webinars': {
    eyebrow: '06 / WEBINARS & LIVE EVENTS',
    headline: 'Sales isn\u2019t calling a room of names. They\u2019re calling a room of ranked interest.',
    lead: 'A registration isn\u2019t an attendee. And an attendee isn\u2019t a lead \u2014 yet. We run the entire event lifecycle as one connected system, not a registration campaign that goes quiet the moment someone signs up.',
    features: [
      { title: 'Registration engine', desc: 'Coordinated email, LinkedIn, and WhatsApp waves driving to a central microsite.' },
      { title: 'Commitment engine', desc: 'Confirmation and calendar sequences that turn a sign-up into a calendar-blocked commitment.' },
      { title: 'Attendance engine', desc: 'Value-led reminders that reinforce why the event matters, not just when it starts.' },
      { title: 'Live execution', desc: 'Real-time polls, Q&A, and moderated engagement to keep the room active, not passive.' },
      { title: 'Post-event follow-up', desc: 'Attendees and no-shows routed differently, ranked by real engagement, then handed to sales.' },
    ],
  },
  'programmatic': {
    eyebrow: '07 / PROGRAMMATIC DISPLAY',
    headline: 'Placements that follow the signal, not a static segment.',
    lead: 'Most buyers aren\u2019t searching for you yet. We make sure you\u2019re already there when they start. Run across industry-leading DSPs, with targeting driven by our own Predictor platform \u2014 the same intent signal that powers everything else we do.',
    features: [
      { title: 'Predictor-driven targeting', desc: 'Placements based on accounts and personas Predictor has already flagged as showing intent.' },
      { title: 'Account-based display', desc: 'Spend concentrated on your named target accounts, not a broad demographic guess.' },
      { title: 'Retargeting', desc: 'Re-engaging hub and microsite visitors while their intent is still warm.' },
      { title: 'Industry-leading DSPs', desc: 'Run across leading demand-side platforms, continuously optimized for performance.' },
      { title: 'One connected system', desc: 'Timed and targeted around the same signal feeding content syndication, LinkedIn, and email.' },
    ],
  },
  'lead-scoring': {
    eyebrow: '08 / LEAD SCORING & INTELLIGENCE',
    headline: 'Your pipeline becomes a priority list, not a guessing game.',
    lead: 'A scoring model that tells your reps exactly who to call first. Every touchpoint across every capability above \u2014 content, calls, ads, events \u2014 feeds one scoring engine, so your team always works from the same prioritized list.',
    features: [
      { title: 'First-party intent data', desc: 'Verified engagement data across every buying persona and job function, not third-party modeled guesses.' },
      { title: 'Explicit + behavioral signal', desc: 'BANT answers, content engagement, ad interaction, and call outcomes all weighted into one score.' },
      { title: 'The lead card', desc: 'Every account handed to your team comes with a full picture of how they engaged, not just a name and title.' },
      { title: 'Continuous re-scoring', desc: 'Scores update as new signal arrives, so a cooling account moves down and a newly active one moves up automatically.' },
    ],
  },
};

export const paidChannels = [
  {
    id: 'meta',
    name: 'Meta Ads',
    sub: 'Facebook · Instagram · Audience Network',
    stat: '3.2B+',
    statLabel: 'monthly active users',
    bestFor: 'B2C & B2B awareness, retargeting, lead gen',
    formats: ['Image & video ads', 'Lead gen forms', 'Reels & Stories', 'Dynamic retargeting'],
    targeting: 'Custom & Lookalike audiences · Interest & behavioural layers · CRM list matching · Pixel-based retargeting · Demographic filters',
    adPreview: 'https://images.unsplash.com/photo-1656164631668-8673eab87b84?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHw0fHxmYWNlYm9vayUyMGFkJTIwcGhvbmV8ZW58MHx8fHwxNzgzNTE1OTM5fDA&ixlib=rb-4.1.0&q=85',
    sampleAd: {
      brand: 'Acme Cloud',
      headline: 'Cut deploy time by 63% — free 14-day trial',
      cta: 'Start Free Trial',
      audience: 'DevOps Directors · SaaS Companies · 500+ employees',
    },
  },
  {
    id: 'google',
    name: 'Google Display',
    sub: 'GDN · YouTube · Performance Max',
    stat: '90%',
    statLabel: 'of global internet users',
    bestFor: 'Brand awareness, intent targeting, remarketing',
    formats: ['Responsive display ads', 'Banner & rich media', 'YouTube pre-roll', 'Performance Max campaigns', 'Gmail sponsored ads'],
    targeting: 'In-market & affinity audiences · Keyword contextual targeting · Customer match (CRM) · Similar audiences · Topic & placement targeting',
    adPreview: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxnb29nbGUlMjBhZHMlMjBkYXNoYm9hcmR8ZW58MHx8fHwxNzgzNTE1OTM5fDA&ixlib=rb-4.1.0&q=85',
    sampleAd: {
      brand: 'Nova Analytics',
      headline: 'The BI platform for data teams — start free',
      cta: 'Book Demo',
      audience: 'Data Leaders · In-market for BI tools · CRM match',
    },
  },
  {
    id: 'linkedin',
    name: 'LinkedIn Ads',
    sub: 'Sponsored · InMail · Dynamic',
    stat: 'B2B',
    statLabel: 'firmographic targeting',
    bestFor: 'B2B lead gen, ABM, decision-maker targeting',
    formats: ['Sponsored content', 'Video ads', 'Message & conversation ads', 'Carousel ads', 'Lead gen forms (native)'],
    targeting: 'Job title, function & seniority · Company name & industry · Revenue & employee size · Account-based lists (TAL) · Skills & group membership',
    adPreview: 'https://images.unsplash.com/photo-1716638013730-11d8f264bfa3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHw0fHxsaW5rZWRpbiUyMGFkfGVufDB8fHx8MTc4MzUxNTkzOXww&ixlib=rb-4.1.0&q=85',
    sampleAd: {
      brand: 'Sentinel Security',
      headline: 'CISOs choose Sentinel — see the ROI report',
      cta: 'Download Report',
      audience: 'CISOs · Financial Services · 1000+ employees · US/UK',
    },
  },
];

export const predictorPillars = [
  {
    id: 'ai',
    title: 'AI-Powered Predictive Analytics',
    desc: 'Surface in-market accounts before they raise their hand.',
    tag: 'PILLAR 01',
  },
  {
    id: 'abm',
    title: 'Account-Based Targeting',
    desc: 'Precision ICP fit at account, contact, and buying-committee level.',
    tag: 'PILLAR 02',
  },
  {
    id: 'whitelabel',
    title: 'White-Label Ready',
    desc: 'Deploy as your brand. Your colors, your domain, your customers.',
    tag: 'PILLAR 03',
  },
];

export const funnelStages = [
  { id: 'tofu', tag: 'TOFU · TOP OF FUNNEL', title: '1.2M Leads', sub: 'Awareness · Content Reach', width: 100 },
  { id: 'mofu', tag: 'MOFU · MID FUNNEL', title: '316K Leads', sub: 'Engagement · Qualified MQLs', width: 68 },
  { id: 'bofu', tag: 'BOFU · BOTTOM', title: '163K Leads', sub: 'SQLs · BANT · Conversion', width: 42 },
];

export const dataIntelligence = {
  total: '~46M',
  totalLabel: 'Total addressable contacts · Globally',
  firstParty: { value: '3.1M', label: '1st-Party Records', desc: 'Proprietary, verified contact records.' },
  thirdParty: { value: '43M', label: '3rd-Party Enriched', desc: "Enriched and unified with the world's leading intelligence sources." },
  sources: ['ZoomInfo', 'Lusha', 'Cognism', 'Bombora', '6sense'],
};

export const microsites = [
  { name: 'Tech-CIO', domain: 'tech-cio.com', subscribers: '756,250', vertical: 'Database, CRM, Dev Tools' },
  { name: 'eTech Intel', domain: 'etechintel.com', subscribers: '3,100,000', vertical: 'Cloud, AI, ML, GenAI' },
  { name: 'HR Wizards', domain: 'hrwizards.net', subscribers: '142,000', vertical: 'HR, Talent, People Ops' },
  { name: 'Pharma Foresight', domain: 'pharmaforesight.net', subscribers: '65,343', vertical: 'Pharma, Life Sciences' },
  { name: 'Your Tech CFO', domain: 'yourtechcfo.com', subscribers: '117,000', vertical: 'Finance, FinOps, ERP' },
];

export const eTechIntel = {
  headline: 'eTechIntel — B2B Tech Lead Generation Engine',
  sub: 'Capture high-intent technology buyers across the full awareness-to-decision journey.',
  metrics: [
    { label: 'Requests · last 7 days', value: '75,378', trend: '+18.4%' },
    { label: 'Data served', value: '5.19 GB', trend: 'sustained' },
    { label: 'Subscribers', value: '3.1M', trend: 'growing' },
    { label: 'Organic pieces / month', value: '15–20', trend: 'per site' },
  ],
  pillars: [
    { n: '01', title: 'Multi-Channel Capture', desc: 'Co-branded registration funnels capturing prospects at peak intent — gated webinars, event registrations, whitepaper downloads, newsletter opt-ins & forms.', touchpoints: '22,578' },
    { n: '02', title: 'Gated Premium Content', desc: "On-demand webinars, buyer's guides, & technology explainers — all behind registration for verified lead capture.", touchpoints: '19,981' },
    { n: '03', title: 'Engaged Tech Community', desc: 'Sustained engagement loop driving repeat visits & remarketing across niche tech topics with 150K+ forum posts.', touchpoints: '5,859' },
    { n: '04', title: 'Event-Driven High-Intent', desc: 'Live and on-demand tech events that surface active buyers ready for sales outreach.', touchpoints: '8,931' },
  ],
  markets: [
    { name: 'United States', pct: 34.2, requests: '25,779' },
    { name: 'India', pct: 14.8, requests: '11,156' },
    { name: 'United Kingdom', pct: 11.3, requests: '8,517' },
    { name: 'Germany', pct: 10.5, requests: '7,914' },
    { name: 'Australia', pct: 10.8, requests: '8,132' },
  ],
  contentSample: [
    { title: 'The CIO Playbook 2026: GenAI in Enterprise', type: 'Whitepaper', downloads: '2,341' },
    { title: 'Post-Cloud Cost Optimization Guide', type: 'Report', downloads: '1,876' },
    { title: 'Zero Trust Architecture — Live Panel', type: 'Webinar', downloads: '1,412' },
    { title: 'Building AI-native Data Platforms', type: 'eBook', downloads: '1,204' },
  ],
};

export const predictorScreens = [
  {
    id: 'accounts',
    tag: 'Account view',
    title: 'In-market Accounts',
    metric: '281',
    metricLabel: 'ready to engage',
  },
  {
    id: 'buying-committee',
    tag: 'Buying committee',
    title: 'Persona-level ICP fit',
    metric: '4.2×',
    metricLabel: 'contact match rate',
  },
  {
    id: 'whitelabel',
    tag: 'White-label ready',
    title: 'Your brand. Your domain.',
    metric: '100%',
    metricLabel: 'custom deployable',
  },
];

export const aiCapabilities = [
  { title: 'AI-Native CSO Ingestion', desc: 'Campaign Spec Object parsed and validated by LLM agents before universe build.', code: 'agent.parse(cso)' },
  { title: 'Predictive Universe Sizing', desc: 'Neural ranking scores every candidate account against ICP fit + intent + timing signals.', code: 'model.rank(accounts)' },
  { title: 'Persona-Aware Generation', desc: 'GPT-powered email and LP variants tuned per seniority tier — VP, Director, Manager.', code: 'llm.generate(persona)' },
  { title: 'Real-time QA Engine', desc: 'AI compares every conversion against original CSO — non-compliant leads quarantined.', code: 'qa.audit(lead)' },
];

export const signals = [
  'Buying Intent', 'Technology Stack', 'Headcount by Location', 'Headcount Growth',
  'Job Postings', 'Headcount by Dept.', 'Company Awards', 'Overall Experience',
  'Funding Events', 'Executive Moves', 'Product Launches', 'Web Traffic',
  'Social Signals', 'M&A Activity', 'Compliance Flags',
];

export const intentAccounts = [
  { rank: 1, name: 'Boeing', score: 93.8 },
  { rank: 2, name: 'Baxter Healthcare', score: 79.2 },
  { rank: 3, name: 'National Australia Bank', score: 69.9 },
  { rank: 4, name: 'AbbVie', score: 67.1 },
  { rank: 5, name: 'Teck Australia', score: 66.7 },
  { rank: 6, name: 'Dassault Systèmes', score: 66.0 },
  { rank: 7, name: 'Lockheed Martin', score: 65.0 },
  { rank: 8, name: 'Cognizant', score: 63.7 },
  { rank: 9, name: 'Genesys', score: 61.1 },
];

export const workflowStages = [
  { n: '01', title: 'Receive & Analyse Brief' },
  { n: '02', title: 'TAL Analysis & Dedup' },
  { n: '03', title: 'Unique Data Universe' },
  { n: '04', title: 'Suppression + CPC Filters' },
  { n: '05', title: 'Database + Creative QA' },
  { n: '06', title: 'Strategy & Channels' },
  { n: '07', title: 'Persona Content Build' },
  { n: '08', title: 'Parallel Execution' },
  { n: '09', title: 'Mid-Flight Optimization' },
  { n: '10', title: 'QA & Delivery' },
];

export const auditStages = [
  {
    stage: 'STAGE 01',
    title: 'Company',
    checks: ['Headcount', 'Geography', 'Industry', 'Suppression — Client'],
  },
  {
    stage: 'STAGE 02',
    title: 'Contact',
    checks: ['Location · Function', 'Dual-employment', 'Open-to-work', 'Tenure'],
  },
  {
    stage: 'STAGE 03',
    title: 'Email · Phone',
    checks: ['Syntax · MX · Deliverability', 'Duplicate check', 'Suppression — Campaign', 'Suppression — Agency'],
  },
  {
    stage: 'STAGE 04',
    title: 'Lead File',
    checks: ['Field completeness', 'Format compliance', 'Delivery template match', 'Client suppression'],
  },
];

export const dashboardKPIs = [
  { label: 'Total Processed', value: '77,857', sub: 'Leads audited this quarter' },
  { label: 'Pass Rate', value: '78.4%', sub: '61,040 leads passed' },
  { label: 'Avg Audit Time', value: '1.8s', sub: 'Per lead, fully automated' },
  { label: 'Data Accuracy', value: '96.1%', sub: 'Verified against ground truth' },
];

export const geoMix = [
  { region: 'N. America', pct: 42, leads: '32,700' },
  { region: 'EMEA', pct: 31, leads: '24,136' },
  { region: 'APAC', pct: 19, leads: '14,793' },
  { region: 'LATAM', pct: 8, leads: '6,228' },
];

export const channelPerf = [
  { channel: 'Meta', impressions: '250,000', clicks: '3,800', leads: 142, spend: '$12,000', cpl: '$85', conv: '3.7%' },
  { channel: 'LinkedIn', impressions: '90,000', clicks: '1,200', leads: 68, spend: '$18,000', cpl: '$265', conv: '5.7%' },
  { channel: 'Email', impressions: '15,000', clicks: '820', leads: 210, spend: '—', cpl: '—', conv: '25.6%' },
  { channel: 'Tele-calling', impressions: '2,400', clicks: '680', leads: 48, spend: '—', cpl: '—', conv: '7.1%' },
  { channel: 'Google Display', impressions: '480,000', clicks: '4,200', leads: 96, spend: '$8,500', cpl: '$89', conv: '2.3%' },
];

export const qualifiedLeads = [
  { n: 1, name: 'Arjun Mehta', company: 'Infosys', title: 'Director of IT', channel: 'LinkedIn', score: 91, grade: 'MQL' },
  { n: 2, name: 'Sarah Chen', company: 'Acme Corp', title: 'VP Engineering', channel: 'Meta', score: 88, grade: 'MQL' },
  { n: 3, name: 'Priya Shah', company: 'FinTech IO', title: 'CTO', channel: 'LinkedIn', score: 84, grade: 'MQL' },
  { n: 4, name: 'David Kim', company: 'Zendesk', title: 'Head of Finance', channel: 'Tele', score: 82, grade: 'MQL' },
  { n: 5, name: 'Fatima Al-Rashid', company: 'MegaCorp', title: 'CIO', channel: 'LinkedIn', score: 80, grade: 'MQL' },
  { n: 6, name: 'Carlos Rivera', company: 'TechCorp', title: 'SVP Finance', channel: 'Email', score: 79, grade: 'Hot' },
  { n: 7, name: 'Olu Adeyemi', company: 'Forge Systems', title: 'VP Engineering', channel: 'Tele', score: 77, grade: 'Hot' },
  { n: 8, name: 'Raj Patel', company: 'StartupX', title: 'CEO', channel: 'Meta', score: 75, grade: 'Hot' },
  { n: 9, name: 'Emma Ross', company: 'NovaSoft', title: 'VP Marketing', channel: 'Email', score: 71, grade: 'Hot' },
  { n: 10, name: 'Mei Lin', company: 'Pacific Tech', title: 'Director IT', channel: 'Email', score: 69, grade: 'Hot' },
  { n: 11, name: 'Lisa Park', company: 'DataWave', title: 'CFO', channel: 'LinkedIn', score: 68, grade: 'Hot' },
  { n: 12, name: 'Andre Dubois', company: 'EuroData', title: 'CFO', channel: 'Tele', score: 67, grade: 'Hot' },
];

export const integrations = {
  partners: ['Integrate', 'LeadsBridge', 'Demand Cloud'],
  crm: ['Salesforce', 'HubSpot', 'Monday CRM'],
  security: ['End-to-end encryption', 'Branded client folders', 'Role-based access & audit logs'],
};

export const pipelineSteps = [
  {
    n: '01',
    title: 'Campaign Setup',
    subtitle: 'Campaign Spec Object (CSO)',
    items: ['Geography · Industry · Revenue', 'Employee size · Tech install base', 'Job title & seniority', 'Target account list', 'Suppression, DNC & competitor exclusions'],
  },
  {
    n: '02',
    title: 'Data Universe',
    subtitle: 'Audience Data Build',
    items: ['AI ingests full campaign spec', 'Returns universe count by segment', 'Applies all suppression & exclusions', 'Exports filtered contact database'],
  },
  {
    n: '03',
    title: 'Content Creation',
    subtitle: 'Creative Production',
    items: ['AI reads persona specs', 'Email variants per seniority tier', 'Landing page variants per segment', 'Templates pushed via API'],
  },
  {
    n: '04',
    title: 'Campaign Execution',
    subtitle: 'Send & Engagement',
    items: ['Contact database imported', 'Audience segmented by persona', 'A/B subject line tests', 'Opens, clicks, bounces tracked'],
  },
  {
    n: '05',
    title: 'QA & Delivery',
    subtitle: 'QA then Delivery',
    items: ['AI QA vs. original CSO', 'Geography, industry, firmographic checks', 'Non-compliant leads quarantined', 'Passed leads mapped & delivered'],
  },
];

export const whyPillars = [
  {
    tag: 'BUILT, OWNED, DEFENSIBLE',
    title: "We don't rent — we own.",
    desc: 'Predictor.com + LeadAudit AI — flagship platforms that turn intent signals into pipeline, in production.',
  },
  {
    tag: 'DATA + REACH',
    title: 'Both. Not either-or.',
    desc: '46M addressable contacts, audited to 96.1% verified data accuracy.',
  },
  {
    tag: 'END-TO-END SLAs',
    title: 'One accountable partner.',
    desc: 'Awareness to BANT — end-to-end SLAs, no vendor stitching.',
  },
  {
    tag: 'FIRST-PARTY REACH',
    title: '4.18M subscribers.',
    desc: 'Across 5 niche microsites — first-party reach, not rented.',
  },
];
